import AppLayout from "@/components/layout/AppLayout";
import { Card } from "@/components/ui/card";
import { Shield, Users, FileText, Calendar, BarChart2, Bell, ClipboardList, UserSquare, UserCog, CalendarDays, Radar, Clock, Package, UserCheck, Building, Sparkles, TrendingUp, Activity, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { LucideIcon } from "lucide-react";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { DashboardSkeleton } from "@/components/ui/dashboard-skeleton";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { QuickActionsHub } from "@/components/dashboard/QuickActionsHub";
import { useKeyboardShortcuts, KeyboardShortcutsIndicator } from "@/components/dashboard/KeyboardShortcuts";
import { AccessibilityProvider } from "@/components/accessibility/AccessibilityProvider";
import { FocusIndicator, focusStyles } from "@/components/accessibility/FocusIndicator";
import { SkipToContent } from "@/components/accessibility/SkipToContent";
import { cn } from "@/lib/utils";
import { useModuleAccess } from "@/hooks/useModuleAccess";
import { useSafeProfile } from "@/hooks/useProfileAccess";
import { useClientProviderEvaluation } from "@/hooks/useShiftaskProviderEvaluation";
import { Star } from "lucide-react";
import { useAdminCustomization } from "@/hooks/useAdminCustomization";

interface Feature {
  title: string;
  description: string;
  icon: LucideIcon;
  path: string;
  external?: boolean;
  adminOnly?: boolean;
  clientHide?: boolean;
  highlight?: boolean;
  category?: string;
  translationKey?: string;
  descriptionKey?: string;
  guardOnly?: boolean;
  guardHide?: boolean;
  openInNewWindow?: boolean;
  gradient?: string;
  iconColor?: string;
  moduleName?: string;
}

const Index = () => {
  console.log('Index component rendering...');
  
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user } = useSupabaseUser();
  const { customization } = useAdminCustomization();
  const {
    isAdmin,
    hasRole,
    isLoading: rolesLoading
  } = useUserRoles();
  const { isEnabled: isModuleEnabled, isLoading: moduleAccessLoading, moduleSettings } = useModuleAccess();
  
  // Fetch user profile data using the safe profile hook
  const { data: userProfile, isLoading: profileLoading } = useSafeProfile(user?.id);
  
  // Check for provider evaluations
  const { data: currentEvaluation } = useClientProviderEvaluation(user?.id || '');
  const hasNoEvaluations = !currentEvaluation;
  
  const [hasNewAlerts, setHasNewAlerts] = useState(false);
  const [alertCategory, setAlertCategory] = useState<string | undefined>(undefined);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Update time every minute for dynamic welcome message
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);
  
  // Enable keyboard shortcuts
  useKeyboardShortcuts();
  
  console.log('User roles:', { isAdmin, isLoading: rolesLoading });

  // Check if user has specific roles with fallback
  const isClient = hasRole ? hasRole('client') : false;
  const isGuard = hasRole ? hasRole('guard') : false;

  console.log('User role flags:', { isClient, isGuard, isAdmin });

  // Get welcome message based on time of day with user's surname
  const getWelcomeMessage = useCallback(() => {
    const hour = currentTime.getHours();
    const surname = userProfile?.last_name ? `, ${userProfile.last_name}` : '';
    
    if (hour < 12) {
      return `${t('goodMorning') || 'Good Morning'}${surname}! ${t('welcomeToDashboard') || 'Welcome to your security dashboard'}`;
    } else if (hour < 17) {
      return `${t('goodAfternoon') || 'Good Afternoon'}${surname}! ${t('welcomeToDashboard') || 'Welcome to your security dashboard'}`;
    } else {
      return `${t('goodEvening') || 'Good Evening'}${surname}! ${t('welcomeToDashboard') || 'Welcome to your security dashboard'}`;
    }
  }, [currentTime, t, userProfile?.last_name]);

  // Enhanced gradient variations with better visual distinction
  const getFeatureGradient = useCallback((index: number) => {
    const variations = [
      "from-gradient-primary via-primary/10 to-transparent",
      "from-gradient-secondary via-purple-500/10 to-transparent", 
      "from-gradient-accent via-emerald-500/10 to-transparent",
      "from-gradient-warning via-orange-500/10 to-transparent",
      "from-gradient-success via-green-500/10 to-transparent",
      "from-gradient-info via-blue-500/10 to-transparent",
      "from-gradient-rose via-rose-500/10 to-transparent",
      "from-gradient-indigo via-indigo-500/10 to-transparent"
    ];
    return variations[index % 8];
  }, []);

  const getFeatureBorder = useCallback((index: number) => {
    const borders = [
      "border-primary/20 hover:border-primary/40",
      "border-purple-200/50 hover:border-purple-300/70",
      "border-emerald-200/50 hover:border-emerald-300/70", 
      "border-orange-200/50 hover:border-orange-300/70",
      "border-green-200/50 hover:border-green-300/70",
      "border-blue-200/50 hover:border-blue-300/70",
      "border-rose-200/50 hover:border-rose-300/70",
      "border-indigo-200/50 hover:border-indigo-300/70"
    ];
    return borders[index % 8];
  }, []);

  const getIconBackground = useCallback((index: number) => {
    const backgrounds = [
      "bg-gradient-to-br from-primary/20 to-primary/10 text-primary shadow-primary/20 dark:from-emerald-600/90 dark:to-emerald-700/80 dark:text-white dark:shadow-emerald-500/30",
      "bg-gradient-to-br from-purple-100/80 to-purple-50/60 text-purple-600 shadow-purple-200/40 dark:from-blue-600/90 dark:to-blue-700/80 dark:text-white dark:shadow-blue-500/30",
      "bg-gradient-to-br from-emerald-100/80 to-emerald-50/60 text-emerald-600 shadow-emerald-200/40 dark:from-purple-600/90 dark:to-purple-700/80 dark:text-white dark:shadow-purple-500/30",
      "bg-gradient-to-br from-orange-100/80 to-orange-50/60 text-orange-600 shadow-orange-200/40 dark:from-orange-600/90 dark:to-orange-700/80 dark:text-white dark:shadow-orange-500/30",
      "bg-gradient-to-br from-green-100/80 to-green-50/60 text-green-600 shadow-green-200/40 dark:from-green-600/90 dark:to-green-700/80 dark:text-white dark:shadow-green-500/30",
      "bg-gradient-to-br from-blue-100/80 to-blue-50/60 text-blue-600 shadow-blue-200/40 dark:from-indigo-600/90 dark:to-indigo-700/80 dark:text-white dark:shadow-indigo-500/30",
      "bg-gradient-to-br from-rose-100/80 to-rose-50/60 text-rose-600 shadow-rose-200/40 dark:from-rose-600/90 dark:to-rose-700/80 dark:text-white dark:shadow-rose-500/30",
      "bg-gradient-to-br from-indigo-100/80 to-indigo-50/60 text-indigo-600 shadow-indigo-200/40 dark:from-slate-600/90 dark:to-slate-700/80 dark:text-white dark:shadow-slate-500/30"
    ];
    return backgrounds[index % 8];
  }, []);

  const baseFeatures: Feature[] = useMemo(() => [
    {
      title: "Personnel Management",
      description: "Manage personnel profiles and assignments",
      icon: Shield,
      path: "/guards",
      translationKey: "guardManagement",
      descriptionKey: "guardManagementDesc"
    },
    {
      title: t("performanceEvaluation") || "Performance Evaluation", 
      description: t("performanceEvaluationDesc") || "Monitor and evaluate performance",
      icon: BarChart2,
      path: "/performance",
      translationKey: "performanceEvaluation",
      descriptionKey: "performanceEvaluationDesc"
    },
    {
      title: t("clientsManagement") || "Clients Management",
      description: t("clientsManagementDesc") || "Manage client relationships",
      icon: UserSquare,
      path: "/clients", 
      adminOnly: true,
      translationKey: "clientsManagement",
      descriptionKey: "clientsManagementDesc"
    },
    {
      title: t("teamOverview") || "Team Overview",
      description: t("teamOverviewDesc") || "View team structure and members",
      icon: Users,
      path: "/teams",
      translationKey: "teamOverview",
      descriptionKey: "teamOverviewDesc"
    },
    {
      title: t("reportsSubmissions") || "Reports & Submissions",
      description: t("reportsSubmissionsDesc") || "Submit and view reports",
      icon: FileText,
      path: "/reports",
      translationKey: "reportsSubmissions",
      descriptionKey: "reportsSubmissionsDesc"
    },
    {
      title: t("workInstructions") || "Work Instructions",
      description: t("workInstructionsDesc") || "View work missions and instructions",
      icon: ClipboardList,
      path: "/missions",
      translationKey: "workInstructions",
      descriptionKey: "workInstructionsDesc"
    },
    {
      title: t("workApplications") || "Work Applications",
      description: t("workApplicationsDesc") || "Submit and manage work applications",
      icon: Package,
      path: "/leave",
      clientHide: true,
      translationKey: "workApplications",
      descriptionKey: "workApplicationsDesc"
    },
    {
      title: t("mySchedule") || "My Schedule",
      description: t("myScheduleDesc") || "View your work schedule",
      icon: Calendar,
      path: "/scheduling",
      clientHide: true,
      translationKey: "mySchedule",
      descriptionKey: "myScheduleDesc"
    },
    {
      title: t("My Teams Schedule") || "My Teams Schedule",
      description: t("My Teams Schedule Description") || "View your assigned teams and their schedules",
      icon: Users,
      path: isGuard ? "/guardteamschedule" : "/clients?tab=teams-scheduling",
      translationKey: "My Teams Schedule",
      descriptionKey: "My Teams Schedule Description"
    },
    {
      title: t("incidentAlerts") || "Incident Alerts",
      description: t("incidentAlertsDesc") || "View security incidents and alerts",
      icon: Bell,
      path: "/alerts",
      adminOnly: true,
      guardHide: true,
      translationKey: "incidentAlerts",
      descriptionKey: "incidentAlertsDesc"
    },
    {
      title: t("hrManagement") || "HR Management",
      description: t("hrManagementDesc") || "Manage candidates, interviews, and HR processes",
      icon: UserCheck,
      path: "/hr",
      adminOnly: true,
      translationKey: "hrManagement",
      descriptionKey: "hrManagementDesc"
    },
    {
      title: t("sentinel") || "Sentinel",
      description: t("patrolMonitoringSystem") || "Patrol monitoring system",
      icon: Radar,
      path: "https://sentinel.shiftask.app/",
      external: true,
      adminOnly: true,
      openInNewWindow: false,
      moduleName: "sentinel"
    },
    {
      title: t("adminTeamMembers") || "Admin Team Members",
      description: t("manageAdministrativeTeam") || "Manage administrative team",
      icon: UserCog,
      path: "/admin-team",
      adminOnly: true,
      translationKey: "adminTeamMembers",
      descriptionKey: "manageAdministrativeTeam"
    },
    {
      title: t("enhancedDashboard") || "Enhanced Dashboard",
      description: t("enhancedDashboardDesc") || "Advanced security and system management",
      icon: Shield,
      path: "/enhanced-dashboard",
      adminOnly: true,
      translationKey: "enhancedDashboard",
      descriptionKey: "enhancedDashboardDesc"
    },
    {
      title: t("facilityPresence") || "Facility Presence",
      description: t("facilityPresenceDesc") || "Monitor facility presence and occupancy",
      icon: Building,
      path: "https://facility.shiftask.app/",
      external: true,
      openInNewWindow: true,
      translationKey: "facilityPresence",
      descriptionKey: "facilityPresenceDesc",
      moduleName: "facility_presence"
    },
    {
      title: t("ethicsCompliance") || "Ethics & Compliance",
      description: t("companyPolicies") || "View company policies and codes of conduct",
      icon: Shield,
      path: "/ethics-compliance",
      translationKey: "ethicsCompliance",
      descriptionKey: "companyPolicies",
      moduleName: "ethics_compliance"
    },
    {
      title: t("applicationDocumentation") || "Application Documentation",
      description: t("applicationDocumentationDesc") || "View complete system documentation",
      icon: FileText,
      path: "/documentation",
      adminOnly: true,
      translationKey: "applicationDocumentation",
      descriptionKey: "applicationDocumentationDesc"
    },
    {
      title: t("providerEvaluations") || "Provider Evaluations",
      description: t("providerEvaluationsDesc") || "Submit and view provider evaluations",
      icon: Star,
      path: "/provider-evaluations",
      clientHide: false,
      guardHide: true,
      translationKey: "providerEvaluations",
      descriptionKey: "providerEvaluationsDesc",
      moduleName: "providerEvaluations"
    },
    {
      title: "Whistleblowers",
      description: "Anonymous reporting system",
      icon: AlertTriangle,
      path: customization?.whistleblower_url || "https://trust.shiftask.app/",
      external: true,
      openInNewWindow: true,
      moduleName: "whistleblowers"
    }
  ], [t, language, isGuard, customization?.whistleblower_url]);

  // Filter features based on user roles and module access - memoized to prevent recalculation
  const filteredFeatures = useMemo(() => {
    return baseFeatures.filter(feature => {
      // Role-based filtering
      if (feature.adminOnly && !isAdmin) return false;
      if (feature.clientHide && isClient) return false;
      if (feature.guardOnly && !isGuard) return false;
      if (feature.guardHide && isGuard) return false;
      
      // Module access filtering
      const featureName = feature.moduleName || 
                         feature.translationKey || 
                         (feature.external ? feature.title.toLowerCase().replace(/\s+/g, '_') :
                         feature.path.replace(/^\//, '').split('/')[0]);
      if (!isModuleEnabled(featureName)) return false;
      
      return true;
    });
  }, [baseFeatures, isAdmin, isClient, isGuard, isModuleEnabled, moduleSettings, moduleAccessLoading]);

  console.log('Filtered features count:', filteredFeatures.length);

  // Update features with alert status - memoized
  const featuresWithAlerts = useMemo(() => {
    return filteredFeatures.map(feature => {
      if (feature.translationKey === "incidentAlerts" && hasNewAlerts) {
        return {
          ...feature,
          highlight: true,
          category: alertCategory
        };
      }
      // Highlight provider evaluations if no evaluation for current quarter
      if (feature.translationKey === "providerEvaluations" && hasNoEvaluations && isClient) {
        return {
          ...feature,
          highlight: true,
          category: "No Evaluation Submitted"
        };
      }
      return feature;
    });
  }, [filteredFeatures, hasNewAlerts, alertCategory, hasNoEvaluations, isClient]);

  const handleCardClick = useCallback((feature: Feature) => {
    console.log('Card clicked:', feature.title, feature.path);
    
    if (feature.external) {
      if (feature.openInNewWindow) {
        window.open(feature.path, '_blank');
      } else {
        window.location.href = feature.path;
      }
    } else {
      navigate(feature.path);
    }
  }, [navigate]);

  // Show loading state while roles are loading
  if (rolesLoading) {
    console.log('Roles still loading...');
    return (
      <AppLayout>
        <DashboardSkeleton />
      </AppLayout>
    );
  }

  console.log('Rendering dashboard with', featuresWithAlerts.length, 'features');

  return (
    <AccessibilityProvider>
      <ErrorBoundary>
        <SkipToContent />
        <FocusIndicator />
        <AppLayout>
        <main id="main-content" className="min-h-screen bg-background dark:bg-gray-950 relative overflow-hidden"
              role="main" 
              aria-label="Security management dashboard"
        >
          {/* Subtle background patterns for dark mode */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/1 via-transparent to-accent/1 dark:from-gray-950/80 dark:via-gray-900/40 dark:to-gray-950/80 pointer-events-none"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-radial from-primary/2 to-transparent dark:from-gray-900/10 dark:to-transparent rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-radial from-accent/2 to-transparent dark:from-gray-900/10 dark:to-transparent rounded-full blur-3xl pointer-events-none"></div>
          
          <div className="relative z-10 space-y-8 sm:space-y-10 md:space-y-12 animate-fadeIn px-4 sm:px-6 md:px-8 lg:px-10 pt-6 pb-12">
            {/* Enhanced Hero Section */}
            <header className="text-center space-y-6 pt-4 sm:pt-8">
              <div className="space-y-4">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <div className="p-4 bg-gradient-to-br from-primary to-primary/80 rounded-2xl shadow-luxury animate-bounce" style={{ animationDuration: '3s' }}>
                    <Shield className="h-8 w-8 text-white" />
                  </div>
                  <div className="h-8 w-px bg-gradient-to-b from-transparent via-primary/50 to-transparent"></div>
                  <div className="p-4 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl shadow-luxury animate-bounce" style={{ animationDuration: '3s', animationDelay: '0.5s' }}>
                    <Activity className="h-8 w-8 text-white" />
                  </div>
                </div>
                
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-foreground via-primary to-accent bg-clip-text text-transparent leading-tight tracking-tight drop-shadow-sm">
                  {t('dashboardOverview') || 'Security Dashboard'}
                </h1>
                
                <div className="flex items-center justify-center gap-2 text-lg sm:text-xl text-foreground/80">
                  <Clock className="h-5 w-5 text-primary animate-pulse" />
                  <span className="font-medium">
                    {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                
                <p className="text-lg sm:text-xl md:text-2xl text-foreground/70 leading-relaxed max-w-3xl mx-auto font-medium">
                  {getWelcomeMessage()}
                </p>
              </div>
              
              {/* Quick Stats */}
              <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8 pt-4">
                 <div className="flex items-center gap-2 px-4 py-2 bg-card dark:bg-gray-900/80 backdrop-blur-sm rounded-full border border-border dark:border-gray-800 shadow-sm">
                   <TrendingUp className="h-4 w-4 text-green-500" />
                   <span className="text-sm font-medium text-foreground dark:text-gray-100">{t('systemActive')}</span>
                 </div>
                 <div className="flex items-center gap-2 px-4 py-2 bg-card dark:bg-gray-900/80 backdrop-blur-sm rounded-full border border-border dark:border-gray-800 shadow-sm">
                   <Users className="h-4 w-4 text-blue-500" />
                   <span className="text-sm font-medium text-foreground dark:text-gray-100">{filteredFeatures.length} {t('featuresAvailable')}</span>
                 </div>
                 {isAdmin && (
                   <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary/10 to-accent/10 dark:from-gray-800/80 dark:to-gray-700/80 backdrop-blur-sm rounded-full border border-primary/30 dark:border-gray-700 shadow-sm">
                     <Sparkles className="h-4 w-4 text-primary" />
                     <span className="text-sm font-medium text-primary dark:text-blue-400">Administrator</span>
                   </div>
                 )}
              </div>
            </header>
            
            {/* Enhanced Quick Actions Hub */}
            <div className="w-full">
              <QuickActionsHub />
            </div>
            
            {/* Features Grid */}
            <section 
              aria-label="Application features"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8"
            >
              {featuresWithAlerts.map((feature, index) => (
                <div key={feature.title} style={{ animationDelay: `${index * 0.1}s` }} className="animate-slide-up">
                  <Card 
                    tabIndex={0}
                    role="button"
                    aria-label={`${feature.title}: ${feature.description}`}
                     className={cn(
                       "group relative overflow-hidden cursor-pointer transform transition-all duration-700 ease-out hover:scale-[1.08] active:scale-[0.95] hover:shadow-luxury active:shadow-enhanced rounded-2xl border-2 touch-manipulation backdrop-blur-sm",
                       "bg-gradient-to-br from-card/95 via-card/90 to-card/50 dark:from-gray-900/90 dark:via-gray-900/80 dark:to-gray-950/70",
                       "hover:bg-gradient-to-br hover:from-card hover:via-card/95 hover:to-card/40 dark:hover:from-gray-800/95 dark:hover:via-gray-900/85 dark:hover:to-gray-950/75",
                       focusStyles.card,
                       feature.highlight 
                         ? 'bg-gradient-to-br from-destructive/10 to-destructive/20 dark:from-red-900/40 dark:to-red-800/50 border-destructive/30 dark:border-red-600/60 animate-pulse shadow-luxury shadow-destructive/20' 
                         : `${getFeatureBorder(index)} hover:shadow-luxury hover:shadow-primary/10 dark:border-gray-800 dark:hover:border-gray-700`,
                       "hover:shadow-2xl hover:border-primary/30 dark:hover:border-gray-700"
                     )}
                  onClick={() => handleCardClick(feature)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleCardClick(feature);
                    }
                  }}
                style={{
                  animationDelay: `${index * 100}ms`
                }}
              >
                {/* Enhanced animated background */}
                <div className="absolute inset-0 opacity-[0.02] dark:opacity-[0.05] pointer-events-none">
                  <div className="absolute top-0 right-0 w-20 h-20 bg-primary rounded-full -translate-y-10 translate-x-10 group-hover:translate-y-0 group-hover:translate-x-0 transition-transform duration-1000"></div>
                  <div className="absolute bottom-0 left-0 w-16 h-16 bg-accent rounded-full translate-y-8 -translate-x-8 group-hover:translate-y-0 group-hover:translate-x-0 transition-transform duration-1000 delay-200"></div>
                  <div className="absolute top-1/2 left-1/2 w-12 h-12 bg-secondary rounded-full -translate-x-1/2 -translate-y-1/2 group-hover:rotate-180 transition-transform duration-1000 delay-100"></div>
                </div>
                
                {/* Enhanced shimmer effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1500 ease-out"></div>
                
                {/* Gradient overlay for depth */}
                <div className={`absolute inset-0 bg-gradient-to-br ${getFeatureGradient(index)} opacity-50 group-hover:opacity-70 transition-opacity duration-300`}></div>
                
                 <div className="relative p-6 sm:p-7 space-y-4 sm:space-y-5 min-h-[220px] sm:min-h-[240px] flex flex-col z-10">
                   <div className="flex items-start justify-between">
                     <div className={cn(
                       "inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-2xl backdrop-blur-sm border border-white/20 group-hover:scale-110 group-hover:rotate-3 active:scale-105 transition-all duration-500 shadow-lg group-hover:shadow-xl",
                       getIconBackground(index)
                     )}>
                       <feature.icon className="w-6 h-6 sm:w-7 sm:h-7 group-hover:scale-110 transition-transform duration-300" />
                     </div>
                      {feature.highlight && (
                        <div className="flex items-center gap-2 animate-fadeIn">
                          <div className="w-3 h-3 bg-destructive rounded-full animate-ping shadow-lg"></div>
                          <span className="px-3 py-1.5 bg-destructive/10 text-destructive text-xs font-bold rounded-xl border border-destructive/20 backdrop-blur-sm shadow-lg">
                            {t('new') || 'New'}
                          </span>
                        </div>
                      )}
                   </div>
                   
                    <div className="space-y-3 flex-1">
                      <h3 className="text-lg sm:text-xl font-bold text-foreground dark:text-gray-100 leading-tight group-hover:text-primary transition-colors duration-300">
                        {feature.title}
                      </h3>
                      <p className="text-sm sm:text-base text-muted-foreground dark:text-gray-300 leading-relaxed line-clamp-2 group-hover:text-muted-foreground/80 dark:group-hover:text-gray-200 transition-colors duration-300">
                        {feature.description}
                      </p>
                      {feature.highlight && feature.category && (
                        <div className="flex items-center gap-3 mt-4 p-3 bg-destructive/10 rounded-xl backdrop-blur-sm border border-destructive/20 shadow-lg animate-fadeIn">
                          <div className="w-2 h-2 bg-destructive rounded-full animate-pulse"></div>
                          <p className="text-sm font-semibold text-destructive">
                            {t('newAlertIn') || 'New alert in'}: {feature.category}
                          </p>
                        </div>
                      )}
                   </div>
                   
                     {/* Enhanced floating action indicator */}
                     <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-2 group-hover:translate-y-0">
                       <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary/80 rounded-full flex items-center justify-center shadow-lg animate-gentle-bounce">
                         <div className="w-2 h-2 bg-primary-foreground rounded-full"></div>
                       </div>
                     </div>
                  </div>
                </Card>
                </div>
              ))}
            </section>
           
           {/* Enhanced Footer */}
            <footer className="text-center py-8 mt-16">
              <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground dark:text-gray-400">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-primary" />
                  <span className="text-foreground/80 dark:text-gray-300">{t('guardlySecuritySystem')}</span>
                </div>
                <div className="h-4 w-px bg-border dark:bg-gray-600"></div>
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-green-500 animate-pulse" />
                  <span className="text-foreground/80 dark:text-gray-300">{t('systemOnline')}</span>
                </div>
              </div>
            </footer>
         </div>
       </main>
       
       {/* Keyboard shortcuts indicator */}
       <KeyboardShortcutsIndicator />
       </AppLayout>
     </ErrorBoundary>
   </AccessibilityProvider>
 );
};

export default Index;
